import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";

const POLL_MAX = 6;

const PRODUCT_LABELS = {
  oct4: "Episode 2: Pretty Girls Love R&B · Oct 4",
  halloween: "The Monsters Ball · Oct 31",
  bundle: "Oct 4 + Halloween Bundle",
};

const BUNDLE_EVENTS = [
  { title: "Pretty Girls Love R&B", detail: "Sunday, Oct 4 · 3–8PM · St. Louis, MO" },
  { title: "The Monsters Ball", detail: "Oct 31 · 7–11PM · St. Louis, MO · $1,000 Best Costume" },
];

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [product, setProduct] = useState("");
  const [qrUrl, setQrUrl] = useState("");
  const [ticketCode, setTicketCode] = useState("");

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let active = true;

    const poll = async () => {
      if (!active) return;
      if (attempts >= POLL_MAX) { setState("pending"); return; }
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setProduct(data.product || "");
          if (data.qr_url) setQrUrl(data.qr_url);
          if (data.ticket_code) setTicketCode(data.ticket_code);
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
        setTimeout(poll, 2000);
      } catch {
        setState("error");
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  const qrBlock = qrUrl ? (
    <div className="mt-8 border border-[#D4AF37]/40 bg-black/50 p-6" data-testid="success-qr-block">
      <img src={qrUrl} alt="Your door QR code" className="w-44 h-44 mx-auto border-4 border-white bg-white" data-testid="success-qr-image" />
      <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] mt-4" data-testid="success-qr-code">Your door pass · {ticketCode}</div>
      <p className="text-[#A19D94] text-xs mt-2">Screenshot this — it's also in your receipt email. Door staff scan it to check your party in.</p>
    </div>
  ) : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="grain-overlay" />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-lg text-center border border-white/10 bg-black/40 backdrop-blur-xl p-12 gold-glow">
        <Link to="/" className="flex items-center justify-center mb-10">
          <img src="/logo-jayatk.png" alt="Jay Atk" className="h-10 w-auto" />
        </Link>

        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-10 h-10 text-[#D4AF37] mx-auto mb-6 animate-spin" />
            <h1 className="font-serif-display text-3xl text-[#FDFBF7]">Confirming your payment…</h1>
            <p className="text-[#A19D94] mt-3">Hang tight, this only takes a moment.</p>
          </div>
        )}

        {state === "paid" && product === "hookah" && (
          <div data-testid="payment-hookah-thanks">
            <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37] flex items-center justify-center gold-glow">
              <CheckCircle2 className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-[#FDFBF7]">Your hookah is <span className="italic text-gradient-gold">locked in</span></h1>
            <p className="text-[#A19D94] mt-4" data-testid="success-hookah-label">
              Preorder confirmed — show your receipt email at the outdoor patio to claim it. Hookahs stay outside. Admission tickets sold separately.
            </p>
          </div>
        )}

        {state === "paid" && product.startsWith("sectionbal:") && (
          <div data-testid="payment-balance-thanks">
            <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37] flex items-center justify-center gold-glow">
              <CheckCircle2 className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-[#FDFBF7]">Fully paid. <span className="italic text-gradient-gold">See you on the 4th</span></h1>
            <p className="text-[#A19D94] mt-4" data-testid="success-balance-label">
              Your bottle balance is settled — your section is 100% locked in. Admission tickets are sold separately.
            </p>
          </div>
        )}

        {state === "paid" && product.startsWith("section:") && (
          <div data-testid="payment-section-thanks">
            <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37] flex items-center justify-center gold-glow">
              <CheckCircle2 className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-[#FDFBF7]">Your section is <span className="italic text-gradient-gold">locked in</span></h1>
            <p className="text-[#A19D94] mt-4" data-testid="success-section-label">
              Deposit confirmed. Your receipt email has your bottle selection and balance details — the balance is due 72 hours before the event. Admission tickets are sold separately.
            </p>
          </div>
        )}

        {state === "paid" && product === "bundle" && (
          <div data-testid="payment-bundle-thanks">
            <div className="w-16 h-16 rounded-full border border-[#D4AF37] flex items-center justify-center mx-auto mb-6">
              <Check className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <h1 className="font-serif-display text-4xl text-[#FDFBF7]">Two nights. <span className="italic text-gradient-gold">Locked in.</span></h1>
            <div className="mt-8 space-y-3 text-left">
              {BUNDLE_EVENTS.map((e) => (
                <div key={e.title} className="border border-white/10 bg-black/40 px-5 py-4" data-testid={`bundle-event-${e.title.includes("Halloween") ? "halloween" : "oct4"}`}>
                  <div className="font-serif-display text-xl text-[#FDFBF7]">{e.title}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#A19D94] mt-1">{e.detail}</div>
                </div>
              ))}
            </div>
            <p className="mt-6 inline-block border border-[#D4AF37]/50 text-[#F4D77E] text-xs uppercase tracking-[0.22em] px-6 py-3" data-testid="bundle-savings">
              Bundle price $30 — you saved $5
            </p>
            {qrBlock}
            <p className="text-[#A19D94] mt-5 text-sm">Bring your receipt email to the door at both events. See you on the dancefloor — twice.</p>
            <Link to="/#request" data-testid="success-request-song"
              className="inline-block mt-8 text-xs uppercase tracking-[0.2em] border border-[#D4AF37] text-[#D4AF37] px-8 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300">
              Now Request a Song
            </Link>
          </div>
        )}

        {state === "paid" && product !== "bundle" && product !== "hookah" && !product.startsWith("section:") && !product.startsWith("sectionbal:") && (
          <div data-testid="payment-paid">
            <div className="w-16 h-16 rounded-full border border-[#D4AF37] flex items-center justify-center mx-auto mb-6">
              <Check className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <h1 className="font-serif-display text-4xl text-[#FDFBF7]">You're in.</h1>
            <p className="text-[#A19D94] mt-3" data-testid="success-product-label">
              {PRODUCT_LABELS[product] ? `${PRODUCT_LABELS[product]} — paid.` : "Your ticket is paid."} See you on the dancefloor.
            </p>
            {qrBlock}
            <Link to="/#request" data-testid="success-request-song"
              className="inline-block mt-8 text-xs uppercase tracking-[0.2em] border border-[#D4AF37] text-[#D4AF37] px-8 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300">
              Now Request a Song
            </Link>
          </div>
        )}

        {state === "pending" && (
          <div data-testid="payment-pending">
            <Loader2 className="w-10 h-10 text-[#D4AF37] mx-auto mb-6" />
            <h1 className="font-serif-display text-3xl text-[#FDFBF7]">Almost there</h1>
            <p className="text-[#A19D94] mt-3">Your payment is processing. If it doesn't confirm shortly, check your email for a receipt.</p>
            <Link to="/" className="inline-block mt-8 text-xs uppercase tracking-[0.2em] text-[#D4AF37] hover:underline">Back to site</Link>
          </div>
        )}

        {state === "error" && (
          <div data-testid="payment-error">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-6" />
            <h1 className="font-serif-display text-3xl text-[#FDFBF7]">Something went wrong</h1>
            <p className="text-[#A19D94] mt-3">We couldn't confirm this payment. Please try again.</p>
            <Link to="/#events" className="inline-block mt-8 text-xs uppercase tracking-[0.2em] border border-[#D4AF37] text-[#D4AF37] px-8 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300">
              Try Again
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
